// Error reporting (Sentry) – must run before other app code
import './instrument.js'

import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import './index.css'
import App from './App.jsx'
import ScrollToTop from './components/ScrollToTop.jsx'
import SeoManager from './components/SeoManager.jsx'
import AuthProvider from './components/AuthProvider.jsx'
import { ThemeProvider } from './theme/ThemeProvider.jsx'
import { LocaleProvider } from './i18n/LocaleProvider.jsx'
import { ToastProvider } from './components/Toast.jsx'
import { Sentry } from './instrument.js'
import ErrorBoundaryFallback from './components/ErrorBoundaryFallback.jsx'

// Analytics (PostHog) — dynamically imported and deferred to idle time so the
// ~50 KB library never blocks first render or inflates mobile bootup / total
// blocking time. Init still runs early enough to capture the initial pageview.
const posthogKey = import.meta.env.VITE_POSTHOG_KEY
const posthogHost = import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com'
if (posthogKey) {
  const initPostHog = () =>
    import('posthog-js').then(({ default: posthog }) => {
      posthog.init(posthogKey, {
        api_host: posthogHost,
        defaults: '2026-01-30',
      })
    })
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    window.requestIdleCallback(initPostHog, { timeout: 4000 })
  } else {
    setTimeout(initPostHog, 2000)
  }
}

const rootOptions =
  typeof Sentry?.reactErrorHandler === 'function'
    ? {
        onUncaughtError: Sentry.reactErrorHandler(),
        onCaughtError: Sentry.reactErrorHandler(),
        onRecoverableError: Sentry.reactErrorHandler(),
      }
    : undefined
const root = createRoot(
  document.getElementById('root'),
  rootOptions,
)

root.render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<ErrorBoundaryFallback />} showDialog={false}>
      <BrowserRouter>
        <ThemeProvider>
          <LocaleProvider>
            <AuthProvider>
              <ToastProvider>
                <ScrollToTop />
                <SeoManager />
                <App />
              </ToastProvider>
            </AuthProvider>
          </LocaleProvider>
        </ThemeProvider>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  </StrictMode>,
)
